import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';
import { UNITS, INCOTERMS } from '../../productOptions.js';
import { Alert, Field } from '../../components/ui.jsx';
import { money, unitLabel, inDays } from './requestUtils.js';

// "Faire une offre / Make an offer": price, quantity, incoterm, port,
// delivery time, validity, message, attachments — or one of my products /
// a Quotation from Documents. Posting again revises the offer.
const CURRENCIES = ['USD', 'CAD', 'EUR'];
export default function OfferForm({ request: r, onDone, onCancel }) {
  const { t, lang, errorText } = useI18n();
  const [f, setF] = useState({ price: '', currency: r.currency || 'USD', quantity: r.quantity ?? '', unit: r.unit || '', incoterm: r.incoterm || '', port: r.destPort || '', deliveryTime: '', validUntil: inDays(14), message: '', listingId: '', documentId: '' });
  const [files, setFiles] = useState([]);
  const [att, setAtt] = useState({ products: [], quotations: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { api('/my/buying-requests/attachables').then(setAtt).catch(() => {}); }, []);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const total = Number(f.price) > 0 && Number(f.quantity) > 0 ? Number(f.price) * Number(f.quantity) : null;
  const unitChoices = UNITS.includes(f.unit) || !f.unit ? UNITS : [f.unit, ...UNITS];

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      let offer = await api(`/my/buying-requests/${r.id}/offer`, { method: 'POST', body: { ...f, lang } });
      if (files.length) {
        const fd = new FormData();
        for (const file of files) fd.append('files', file);
        offer = await api(`/my/buying-requests/offers/${offer.id}/files`, { method: 'POST', form: fd });
      }
      onDone(offer);
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  }

  return (
    <form className="offer-form" onSubmit={submit}>
      <div className="row2">
        <Field label={t('buying.offer.price')} htmlFor="of-price"><input id="of-price" type="number" min="0" step="0.01" inputMode="decimal" required value={f.price} onChange={set('price')} /></Field>
        <Field label={t('buying.offer.currency')} htmlFor="of-cur"><select id="of-cur" value={f.currency} onChange={set('currency')}>{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
      </div>
      <div className="row2">
        <Field label={t('buying.offer.quantity')} htmlFor="of-qty"><input id="of-qty" type="number" min="0" step="0.01" inputMode="decimal" required value={f.quantity} onChange={set('quantity')} /></Field>
        <Field label={t('buying.offer.unit')} htmlFor="of-unit"><select id="of-unit" value={f.unit} onChange={set('unit')}>{unitChoices.map((u) => <option key={u} value={u}>{unitLabel(u, t)}</option>)}</select></Field>
      </div>
      {total !== null && <p className="offer-total">{t('buying.offer.total')} : <strong>{money(total, f.currency, lang)}</strong></p>}
      <div className="row2">
        <Field label={t('buying.offer.incoterm')} optional htmlFor="of-inco"><select id="of-inco" value={f.incoterm} onChange={set('incoterm')}><option value="">—</option>{INCOTERMS.map((c) => <option key={c}>{c}</option>)}</select></Field>
        <Field label={t('buying.offer.port')} optional htmlFor="of-port"><input id="of-port" maxLength={120} value={f.port} onChange={set('port')} /></Field>
      </div>
      <div className="row2">
        <Field label={t('buying.offer.deliveryTime')} optional htmlFor="of-del"><input id="of-del" maxLength={60} placeholder={t('buying.offer.deliveryTimePh')} value={f.deliveryTime} onChange={set('deliveryTime')} /></Field>
        <Field label={t('buying.offer.validUntil')} optional htmlFor="of-valid"><input id="of-valid" type="date" min={inDays(0)} value={f.validUntil} onChange={set('validUntil')} /></Field>
      </div>
      <Field label={t('buying.offer.message')} optional htmlFor="of-msg"><textarea id="of-msg" rows={4} maxLength={4000} placeholder={t('buying.offer.messagePh')} value={f.message} onChange={set('message')} /></Field>
      {att.products.length > 0 && (
        <Field label={t('buying.offer.attachProduct')} optional htmlFor="of-prod"><select id="of-prod" value={f.listingId} onChange={set('listingId')}><option value="">{t('buying.offer.none')}</option>{att.products.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}</select></Field>
      )}
      {att.quotations.length > 0 && (
        <Field label={t('buying.offer.attachQuote')} optional htmlFor="of-doc"><select id="of-doc" value={f.documentId} onChange={set('documentId')}><option value="">{t('buying.offer.none')}</option>{att.quotations.map((d) => <option key={d.id} value={d.id}>{d.ref} — {d.title}</option>)}</select></Field>
      )}
      <Field label={t('buying.offer.files')} optional htmlFor="of-files"><input id="of-files" type="file" multiple accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => setFiles([...e.target.files].slice(0, 4))} /></Field>
      <Alert>{error}</Alert>
      <div className="req-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? t('buying.offer.sending') : t('buying.offer.send')}</button>
        {onCancel && <button type="button" className="btn btn-ghost" onClick={onCancel}>{t('buying.f.cancel')}</button>}
        <Link to="/account/messages" className="small muted">{t('accountNav.messages')}</Link>
      </div>
    </form>
  );
}
